/**
 * POSStaffDashboard - Main dashboard for POS staff after login
 * Shows menu options based on permissions
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CreditCard, ShoppingCart, Receipt, User, LogOut,
  DollarSign, TrendingUp, Package, BarChart3, Zap
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

// Sub-components
import POSInterface from './POSInterface';
import OrdersManagement from './OrdersManagement';
import QuickSaleMode from './QuickSaleMode';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const POSStaffDashboard = () => {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState('dashboard');
  const [stats, setStats] = useState(null);

  const token = getToken();
  const permissions = JSON.parse(localStorage.getItem('dining_staff_permissions') || '[]');
  const staffName = localStorage.getItem('dining_staff_name') || 'Staff';
  const staffRole = localStorage.getItem('dining_staff_role') || 'staff';

  // Check if admin user - grant all permissions
  const isAdmin = token && !localStorage.getItem('dining_staff_type');
  
  // Check permissions - admins have full access
  const canCreateOrders = isAdmin || permissions.includes('pos.create_orders') || permissions.includes('*');
  const canApplyDiscounts = isAdmin || permissions.includes('pos.apply_discounts') || permissions.includes('*');
  const canProcessPayments = isAdmin || permissions.includes('pos.process_payments') || permissions.includes('*');
  const canViewReports = isAdmin || permissions.includes('pos.view_reports') || permissions.includes('*');
  const canRefund = isAdmin || permissions.includes('pos.refunds') || permissions.includes('*');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/dining/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          setStats(await res.json());
        }
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      }
    };
    fetchStats();
  }, [token]);

  const handleLogout = () => {
    localStorage.removeItem('eventflow_token');
    localStorage.removeItem('token');
    localStorage.removeItem('dining_staff_token');
    localStorage.removeItem('dining_staff_type');
    localStorage.removeItem('dining_staff_role');
    localStorage.removeItem('dining_staff_name');
    localStorage.removeItem('dining_staff_permissions');
    toast.success('Logged out successfully');
    navigate('/dining/login');
  };

  const roleNames = {
    'pos_manager': 'POS Manager',
    'cashier': 'Cashier',
    'order_taker': 'Order Taker',
    'viewer': 'Viewer'
  };

  // Render based on active view
  if (activeView === 'quicksale') {
    return <QuickSaleMode onExitQuickMode={() => setActiveView('dashboard')} />;
  }

  if (activeView === 'pos') {
    return (
      <div className="min-h-screen">
        <div className="fixed top-4 left-4 z-50">
          <Button 
            variant="outline" 
            size="sm"
            className="bg-white shadow-lg"
            onClick={() => setActiveView('dashboard')}
          >
            ← Back to Dashboard
          </Button>
        </div>
        <POSInterface />
      </div>
    );
  }

  if (activeView === 'orders') {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <Button 
            variant="outline" 
            size="sm"
            className="mb-4"
            onClick={() => setActiveView('dashboard')}
          >
            ← Back to Dashboard
          </Button>
          <OrdersManagement />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50" data-testid="pos-staff-dashboard">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                <CreditCard className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">POS Portal</h1>
                <p className="text-sm text-gray-500">Welcome back, {staffName}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Badge className="bg-blue-100 text-blue-800">
                {roleNames[staffRole] || staffRole}
              </Badge>
              <Button variant="ghost" size="sm" onClick={() => setActiveView('profile')}>
                <User className="h-4 w-4 mr-2" />
                Profile
              </Button>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {activeView === 'profile' ? (
          <Card className="max-w-md mx-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                My Profile
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="font-medium">{staffName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Role</p>
                <Badge className="bg-blue-100 text-blue-800">
                  {roleNames[staffRole] || staffRole}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-2">Permissions</p>
                <div className="flex flex-wrap gap-1">
                  {permissions.map((perm, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs">
                      {perm.split('.')[1] || perm}
                    </Badge>
                  ))}
                </div>
              </div>
              <Button className="w-full mt-4" onClick={() => setActiveView('dashboard')}>
                Back to Dashboard
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Quick Stats */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Today's Orders</p>
                        <p className="text-3xl font-bold text-blue-600">{stats.today_orders || 0}</p>
                      </div>
                      <ShoppingCart className="h-8 w-8 text-blue-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Today's Revenue</p>
                        <p className="text-3xl font-bold text-green-600">${stats.today_revenue?.toFixed(2) || '0.00'}</p>
                      </div>
                      <DollarSign className="h-8 w-8 text-green-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Pending</p>
                        <p className="text-3xl font-bold text-yellow-600">{stats.pending_orders || 0}</p>
                      </div>
                      <Receipt className="h-8 w-8 text-yellow-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Menu Items</p>
                        <p className="text-3xl font-bold text-purple-600">{stats.available_items || 0}</p>
                      </div>
                      <Package className="h-8 w-8 text-purple-500" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Menu Options */}
            <h2 className="text-lg font-semibold text-gray-700 mb-4">Quick Actions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Quick Sale Mode - Featured */}
              {canCreateOrders && (
                <Card 
                  className="cursor-pointer hover:shadow-lg transition-all hover:scale-102 border-2 border-yellow-400 bg-gradient-to-br from-yellow-50 to-amber-50"
                  onClick={() => setActiveView('quicksale')}
                  data-testid="quick-sale-card"
                >
                  <CardHeader>
                    <div className="w-14 h-14 bg-yellow-500 rounded-xl flex items-center justify-center mb-2">
                      <Zap className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-yellow-800">Quick Sale</CardTitle>
                    <CardDescription>Fast checkout with one-tap ordering</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button className="w-full bg-yellow-500 hover:bg-yellow-600 text-white">
                      Start Quick Sale
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* New Order */}
              {canCreateOrders && (
                <Card 
                  className="cursor-pointer hover:shadow-lg transition-all hover:scale-102 border-2 border-transparent hover:border-blue-300"
                  onClick={() => setActiveView('pos')}
                >
                  <CardHeader>
                    <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-2">
                      <ShoppingCart className="h-8 w-8 text-blue-600" />
                    </div>
                    <CardTitle>Full POS</CardTitle>
                    <CardDescription>Full POS with all menu items & options</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button className="w-full bg-blue-500 hover:bg-blue-600">
                      Open POS
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* View Orders */}
              <Card 
                className="cursor-pointer hover:shadow-lg transition-all hover:scale-102 border-2 border-transparent hover:border-green-300"
                onClick={() => setActiveView('orders')}
              >
                <CardHeader>
                  <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-2">
                    <Receipt className="h-8 w-8 text-green-600" />
                  </div>
                  <CardTitle>View Orders</CardTitle>
                  <CardDescription>View and manage today's orders</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button variant="outline" className="w-full">
                    View Orders
                  </Button>
                </CardContent>
              </Card>

              {/* Reports - Only for managers */}
              {canViewReports && (
                <Card
                  className="cursor-pointer hover:shadow-lg transition-all hover:scale-102 border-2 border-transparent hover:border-purple-300"
                  onClick={() => (window.location.href = '/admin/bank')}
                  data-testid="pos-reports-tile"
                >
                  <CardHeader>
                    <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-2">
                      <BarChart3 className="h-8 w-8 text-purple-600" />
                    </div>
                    <CardTitle>Reports</CardTitle>
                    <CardDescription>Sales · Revenue · P&amp;L (Bank Dashboard)</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button variant="outline" className="w-full" data-testid="pos-reports-open-btn">
                      Open Bank Reports
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default POSStaffDashboard;
